import { Card, CardMedia, Typography, Box } from "@mui/material";
import { AutoTextSize } from "auto-text-size";
import pluralize from "pluralize";
import React, {
  ComponentPropsWithoutRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import { uniqBy } from "lodash";
import placeholder from "../../../../assets/video_placeholder.png";
import { IEmbed } from "./interface";
import { cldUrl } from "../../../../utils/cloudinary";
import { MenuDrawer } from "../../../../components/MenuOptions";
import { ConfirmDeleteModal } from "./ConfirmDelete";
import { StoryForm } from "../components/selectedEmbedV2/storyFormCotainer/StoryForm";
import { IconProduct, IconUser } from "../../../../utils/icons";
import { truncateStyle } from "../../productsV2/productListView/constants";

interface EmbedCardProps extends ComponentPropsWithoutRef<"div"> {
  onClick: () => void;
  onDelete?: () => void;
  refetch?: () => void;
  embed: IEmbed;
}

export function EmbedCard(props: EmbedCardProps) {
  const { embed, onDelete, refetch, ...rest } = props;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sources, setSources] = useState<Array<string>>([]);
  const [hoverState, setHoverState] = useState<boolean>(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  useEffect(() => {
    const urls = embed.files.map((e) => {
      const url = e.location || e.reviewS3File?.location || placeholder;
      return url?.replace(".mp4", "_thumbnail.png") || "";
    });
    setSources(urls);
  }, [embed]);

  const pros = useMemo(() => {
    const owners = (embed.files || [])
      .map((video) => video.videoPros?.[0])
      .filter((e) => !!e);
    const uniqueOwners = uniqBy(owners, "userid");
    return {
      avatars: uniqueOwners.map((e) => e?.avatar)?.slice(0, 3),
      names: uniqueOwners
        .map((e) => e?.displayName)
        .slice(0, 2)
        .join(uniqueOwners.length > 2 ? " , " : " and "),
      otherCount: uniqueOwners.slice(2).length,
    };
  }, [embed]);

  const storyProsWidth = useMemo(
    () => 20 + 10 * (pros.avatars.length - 1),
    [pros]
  );

  return (
    <Card
      elevation={hoverState ? 10 : 0}
      onMouseOver={() => setHoverState(true)}
      onMouseLeave={() => setHoverState(false)}
      className="border border-border-token w-full cursor-pointer"
      {...rest}
    >
      <div className="p-5 flex flex-col align-start gap-4">
        <Box className="flex justify-between items-center gap-2">
          <Typography
            className="w-full font-normal !text-md !leading-5 !font-extrabold text-text-1 hover:text-accent-text hover:underline"
            sx={truncateStyle(1)}
          >
            {embed.name}
          </Typography>
          <MenuDrawer
            showBelow
            title="story"
            id={embed.id}
            onDelete={() => setShowDeleteModal(true)}
            onEdit={() => setShowSettingsModal(true)}
            onMenuClose={() => setHoverState(false)}
          />
        </Box>
        <Box className="relative h-[180px] flex items-center justify-center">
          {sources.map((source, index) => (
            <CardMedia
              component="img"
              image={source}
              alt=""
              className="rounded-lg absolute"
              key={`image-${embed.id}-${source}`}
              style={{
                top: `${index * 10}px`,
                maxWidth: `calc(100% - ${
                  ((sources?.length || 0) - index - 1) * 20
                }px)`,
                height: `${187 - ((sources?.length || 0) + index * 10)}px`,
              }}
            />
          ))}
          {!sources.length && (
            <Box className="flex h-[175px] w-full items-center justify-center bg-tint rounded-xl">
              <IconProduct />
            </Box>
          )}
          <Box className="absolute bottom-2 right-2 flex gap-3">
            <Typography
              className="bg-surface rounded-lg px-2"
              color="text.secondary"
            >
              {embed?.files?.length === 1
                ? `${embed?.files?.length} Video`
                : `${embed?.files?.length} Videos`}
            </Typography>
          </Box>
        </Box>
        <Box className="flex gap-1">
          <>
            <Box className="relative" sx={{ minWidth: `${storyProsWidth}px` }}>
              {pros?.avatars?.map((avatar, index) =>
                avatar ? (
                  <CardMedia
                    key={`story-avatar-${avatar}-${index}`}
                    component="img"
                    image={cldUrl(avatar, { w: 40, h: 40, crop: "fill", gravity: "face" })}
                    alt=""
                    className="rounded-xl border border-surface shrink-0 absolute top-0"
                    sx={{ left: `${10 * index}px`, width: 20, height: 20 }}
                  />
                ) : (
                  <Box
                    className="flex justify-center items-center rounded-xl border border-surface absolute top-0 bg-tint"
                    sx={{ left: `${10 * index}px`, width: 20, height: 20 }}
                  >
                    <IconUser width={12} height={12} />
                  </Box>
                )
              )}
            </Box>
            <Box
              className="flex !items-center"
              sx={{
                minWidth: `calc(100% - ${storyProsWidth + 5}px)`,
                height: 20,
              }}
            >
              {(!!pros.names.length || !!pros.otherCount) && (
                <AutoTextSize maxFontSizePx={12} className="text-muted">
                  {pros?.names}
                  {!!pros?.otherCount &&
                    ` and ${pluralize("other expert", pros.otherCount, true)}`}
                </AutoTextSize>
              )}
            </Box>
          </>
        </Box>
      </div>
      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onOkay={() => {
          onDelete?.();
          setShowDeleteModal(false);
        }}
      />
      {embed && showSettingsModal && (
        <StoryForm
          embed={embed}
          onClose={() => setShowSettingsModal(false)}
          onComplete={() => refetch?.()}
        />
      )}
    </Card>
  );
}
